import { FunctionComponent, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import useAsync from 'react-use/lib/useAsync';
import useAsyncFn from 'react-use/lib/useAsyncFn';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import { isPluginEnabled, StepTimeline } from '@internal/plugin-shared-react';
import { sonarqubeApiRef } from '../../../api/sonarqube-api';
import { SonarQubeServiceGroup } from '../../../api/service-groups/types';
import {
  isValidApplicationDescription,
  isValidApplicationName,
} from '../../../utils/service-groups/keys';
import {
  CreateSonarQubeServiceGroup,
  sonarQubeServiceGroupSteps,
} from '../CreateSonarQubeServiceGroupPage/CreateSonarQubeServiceGroupPage';
import shared from '../../../styles.module.css';
import styles from './UpdateSonarQubeServiceGroupPage.module.css';

// Like useCreateSonarQubeServiceGroup, but prefilled from the group, with the key locked.
const useUpdateSonarQubeServiceGroup = (
  group: SonarQubeServiceGroup,
  onUpdated: () => void,
) => {
  const sonarqubeApi = useApi(sonarqubeApiRef);
  const [applicationName, setApplicationName] = useState(group.applicationName);
  const [applicationDescription, setApplicationDescription] = useState(
    group.applicationDescription,
  );

  const values = {
    applicationKey: group.applicationKey,
    applicationName: applicationName.trim(),
    applicationDescription: applicationDescription.trim(),
  };
  const valid = {
    applicationKey: true,
    applicationName: isValidApplicationName(values.applicationName),
    applicationDescription: isValidApplicationDescription(
      values.applicationDescription,
    ),
  };
  const nameChanged = values.applicationName !== group.applicationName;
  const descriptionChanged =
    values.applicationDescription !== group.applicationDescription;

  const [submitState, submit] = useAsyncFn(async () => {
    // Only changed fields are sent; the service leaves the others as they are.
    await sonarqubeApi.updateServiceGroup(group.id, {
      ...(nameChanged ? { applicationName: values.applicationName } : {}),
      ...(descriptionChanged
        ? { applicationDescription: values.applicationDescription }
        : {}),
    });
    onUpdated();
  }, [
    sonarqubeApi,
    group.id,
    nameChanged,
    descriptionChanged,
    values.applicationName,
    values.applicationDescription,
  ]);

  /** `step` is 1-based within `sonarQubeServiceGroupSteps`. */
  const isStepValid = (step: number) =>
    step === 1
      ? valid.applicationName &&
        valid.applicationDescription &&
        (nameChanged || descriptionChanged)
      : !submitState.loading;

  return {
    values,
    valid,
    inputs: { applicationName, applicationDescription },
    setApplicationKey: () => {},
    setApplicationName,
    setApplicationDescription,
    isStepValid,
    submit,
    submitState,
  };
};

const UpdateFlow = ({
  group,
  onDone,
}: {
  group: SonarQubeServiceGroup;
  onDone: () => void;
}) => {
  const [activeStep, setActiveStep] = useState(1);
  const form = useUpdateSonarQubeServiceGroup(group, onDone);

  return (
    <StepTimeline
      steps={sonarQubeServiceGroupSteps}
      activeStep={activeStep}
      onCancel={onDone}
      onPrevious={() => setActiveStep(step => Math.max(step - 1, 1))}
      onNext={() =>
        activeStep === sonarQubeServiceGroupSteps.length
          ? form.submit()
          : setActiveStep(step => step + 1)
      }
      isNextDisabled={!form.isStepValid(activeStep)}
      isSubmitting={form.submitState.loading}
      submitLabel="Update tooling group"
    >
      <CreateSonarQubeServiceGroup
        step={activeStep}
        form={form}
        previous={group}
      />
    </StepTimeline>
  );
};

interface UpdateSonarQubeServiceGroupPageProps {
  /** Called once the service group loads, so the app shell can show its name in the breadcrumb. */
  onServiceGroupLoaded?: (serviceGroup: SonarQubeServiceGroup) => void;
}

/** Route: /product-teams/:id/tooling-groups/sonarqube/:serviceGroupId/edit */
export const UpdateSonarQubeServiceGroupPage: FunctionComponent<
  UpdateSonarQubeServiceGroupPageProps
> = ({ onServiceGroupLoaded }) => {
  const configApi = useApi(configApiRef);
  const sonarqubeApi = useApi(sonarqubeApiRef);
  const navigate = useNavigate();
  const { id = '', serviceGroupId = '' } = useParams();
  const {
    value: serviceGroup,
    loading,
    error,
  } = useAsync(
    () => sonarqubeApi.getServiceGroup(serviceGroupId),
    [sonarqubeApi, serviceGroupId],
  );

  useEffect(() => {
    if (serviceGroup) {
      onServiceGroupLoaded?.(serviceGroup);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceGroup]);

  if (!isPluginEnabled(configApi, 'sonarqube')) {
    return <Navigate to="/" replace />;
  }
  if (loading) {
    return <Progress />;
  }
  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  return (
    <div className={styles.body}>
      {serviceGroup ? (
        <UpdateFlow
          group={serviceGroup}
          onDone={() =>
            navigate(
              `/product-teams/${encodeURIComponent(
                id,
              )}/tooling-groups/sonarqube/${encodeURIComponent(serviceGroupId)}`,
            )
          }
        />
      ) : (
        <span className={shared.hintText}>Tooling group not found.</span>
      )}
    </div>
  );
};
